/** Mirrors apps/server/src/modules/config-institucional/jornada.util.ts */

import { IJornadaConfig } from '../interfaces/configuracao';

/** Week slots: index 0 is Sunday and 6 is Saturday, as on the server. */
export const DIAS_NA_SEMANA = 7;

/**
 * i18n key per weekday index, so index 0 stays Sunday as on the server. Lives here
 * because more than one screen names a weekday: the week editor in Configurações and
 * the idle date of the Visão executiva.
 */
export const DIA_KEYS: ReadonlyArray<string> = [
  'domingo',
  'segunda',
  'terca',
  'quarta',
  'quinta',
  'sexta',
  'sabado',
];

/** Highest minutes a single day accepts, the same ceiling the server DTO enforces. */
export const MINUTOS_MAX_POR_DIA = 1440;

/** Same starting point the server seeds: our proposal, still to be confirmed. */
export const PROGRAMAR_HOJE_PADRAO = true;

/** Indexes the legacy `minSegQui` covered: Monday to Thursday. */
const SEGUNDA_A_QUINTA = [1, 2, 3, 4];

/** Friday, the only day with its own shift length in the legacy schedule. */
const SEXTA = 5;

/** Minutes of the day a `HH:MM` means; `null` when it is not a readable time. */
export function minutosDoHorario(hhmm: string | undefined): number | null {
  const partes = (hhmm ?? '').split(':');
  const horas = Number(partes[0]);
  const minutos = Number(partes[1]);
  if (partes.length !== 2 || !Number.isInteger(horas) || !Number.isInteger(minutos)) {
    return null;
  }
  return horas < 0 || horas > 23 || minutos < 0 || minutos > 59 ? null : horas * 60 + minutos;
}

/**
 * When the shift of a weekday ENDS, in minutes of the day. Friday has its own end, as
 * on the server; `null` when the journey does not say the hours of the day.
 */
export function fimDoExpedienteEmMinutos(
  diaSemana: number,
  jornada: Partial<IJornadaConfig> | undefined,
): number | null {
  const hhmm = diaSemana === SEXTA ? jornada?.jornadaFimSexta : jornada?.jornadaFim;
  return minutosDoHorario(hhmm);
}

/** `870` reads as `14:30`: the clock the screen writes. */
export function horaDoMinuto(minutos: number): string {
  const horas = Math.floor(minutos / 60);
  const resto = Math.round(minutos % 60);
  return `${String(horas).padStart(2, '0')}:${String(resto).padStart(2, '0')}`;
}

/**
 * Productive minutes of each weekday. Uses the configured week when the document
 * has all seven slots, otherwise derives it from the legacy fields so the editor
 * opens with what the engine is using right now.
 */
export function minutosPorDiaSemana(jornada: Partial<IJornadaConfig> | undefined): Array<number> {
  const configurada = jornada?.minutosPorDiaSemana;
  if (Array.isArray(configurada) && configurada.length === DIAS_NA_SEMANA) {
    return [...configurada];
  }
  const semana = new Array<number>(DIAS_NA_SEMANA).fill(0);
  for (const dia of SEGUNDA_A_QUINTA) {
    semana[dia] = jornada?.minSegQui ?? 0;
  }
  semana[SEXTA] = jornada?.minSex ?? 0;
  return semana;
}

export interface ISemanaResumo {
  /** Average minutes of a working day, rounded only at the end. */
  mediaMinutos: number;
  diasTrabalhados: number;
}

/**
 * Whether the queue of each cell starts on today itself. A document saved before
 * the field existed shows the seed, the same one the server falls back to.
 */
export function programaODiaDeHoje(jornada: Partial<IJornadaConfig> | undefined): boolean {
  return jornada?.programarHoje ?? PROGRAMAR_HOJE_PADRAO;
}

/** Half a day still counts as a whole working day, as the engine does. */
export function resumoSemana(minutos: Array<number>): ISemanaResumo {
  const trabalhados = minutos.filter((minutosDia) => minutosDia > 0);
  if (trabalhados.length === 0) {
    return { mediaMinutos: 0, diasTrabalhados: 0 };
  }
  const total = trabalhados.reduce((soma, minutosDia) => soma + minutosDia, 0);
  return { mediaMinutos: Math.round(total / trabalhados.length), diasTrabalhados: trabalhados.length };
}
